// Tiny markdown subset → React renderer. No deps.
// Supports: ## H2, ### H3, **bold**, _italic_, [text](url), bullet lists, paragraphs, ![alt](src) images.
import React from "react";

function renderInline(text) {
  // Escape any raw HTML
  const safe = String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Tokens: images, links, bold, italic, line breaks
  const out = [];
  let i = 0;
  const re = /(\!\[[^\]]*\]\([^)]+\))|(\[[^\]]+\]\([^)]+\))|(\*\*[^*]+\*\*)|(_[^_]+_)/g;
  let m;
  let key = 0;
  while ((m = re.exec(safe)) !== null) {
    if (m.index > i) out.push(safe.slice(i, m.index));
    const t = m[0];
    if (t.startsWith("![")) {
      const mm = t.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
      out.push(
        <img
          key={`md-${key++}`}
          src={mm[2]}
          alt={mm[1]}
          loading="lazy"
          decoding="async"
          className="my-8 w-full h-auto border border-slate-200"
        />
      );
    } else if (t.startsWith("[")) {
      const mm = t.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      out.push(
        <a key={`md-${key++}`} href={mm[2]} target="_blank" rel="noreferrer" className="underline hover:text-[#C5A059]">
          {mm[1]}
        </a>
      );
    } else if (t.startsWith("**")) {
      out.push(<strong key={`md-${key++}`}>{renderInline(t.slice(2, -2))}</strong>);
    } else if (t.startsWith("_")) {
      out.push(<em key={`md-${key++}`}>{renderInline(t.slice(1, -1))}</em>);
    }
    i = re.lastIndex;
  }
  if (i < safe.length) out.push(safe.slice(i));
  return out;
}

export default function Markdown({ text }) {
  if (!text) return null;
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.startsWith("### ")) {
      blocks.push(
        <h3 key={i} className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-12 mb-4 tracking-tight">
          {renderInline(line.slice(4))}
        </h3>
      );
      i++;
    } else if (line.startsWith("## ")) {
      blocks.push(
        <h2 key={i} className="font-display text-3xl md:text-4xl font-bold text-slate-900 mt-14 mb-5 tracking-tight">
          {renderInline(line.slice(3))}
        </h2>
      );
      i++;
    } else if (line.startsWith("# ")) {
      blocks.push(
        <h2 key={i} className="font-display text-4xl md:text-5xl font-bold text-slate-900 mt-14 mb-6 tracking-tight">
          {renderInline(line.slice(2))}
        </h2>
      );
      i++;
    } else if (/^[-*] /.test(line)) {
      const items = [];
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(lines[i].slice(2));
        i++;
      }
      blocks.push(
        <ul key={`u${i}`} className="list-disc pl-6 my-5 space-y-2 text-slate-700 leading-relaxed">
          {items.map((it, j) => <li key={j}>{renderInline(it)}</li>)}
        </ul>
      );
    } else if (line.trim() === "") {
      i++;
    } else {
      // paragraph — consume contiguous non-empty, non-heading, non-list lines
      const para = [];
      while (
        i < lines.length &&
        lines[i].trim() !== "" &&
        !lines[i].startsWith("#") &&
        !/^[-*] /.test(lines[i])
      ) {
        para.push(lines[i]);
        i++;
      }
      blocks.push(
        <p key={`p${i}`} className="my-5 text-slate-700 leading-[1.8] text-lg">
          {renderInline(para.join(" "))}
        </p>
      );
    }
  }
  return <div className="prose-pbd">{blocks}</div>;
}
